import { useEffect, useMemo, useState } from 'react'
import { ExternalLink, Save } from 'lucide-react'
import { api } from '@/util/api'
import { Button, ButtonLink, Card, CopyField, TextAreaField, TextField, useToast } from '@/components/play'
import type { EventOnboarding, EventResource } from '@/types/events'

interface Props {
  eventId: string
  event: EventResource | null
  onSaved?: (next: EventResource) => void
}

interface ApiError {
  response?: { data?: { error?: { message?: string; errors?: string[] | Record<string, string[]> } } }
}

const TEXT_FIELDS: { key: keyof EventOnboarding; label: string; help: string }[] = [
  { key: 'intro', label: 'What is StreamBridge?', help: 'The welcome, and what StreamBridge is.' },
  { key: 'why', label: 'Why it’s great to be part of it', help: 'What the camera operator or sponsor gets from it.' },
  { key: 'how_to_stream', label: 'How you stream', help: 'Open app, scan QR, go live.' },
  { key: 'iphone_requirements', label: 'What you need on your iPhone', help: 'The phone and app they need.' },
  { key: 'activation', label: 'How to find your QR code', help: 'Where to find the camera’s QR code before opening the app.' },
  { key: 'testing_intro', label: 'Preview step', help: 'Shown under the Comrades Map preview step.' },
]

const LINK_FIELDS: { key: keyof EventOnboarding; label: string; placeholder: string }[] = [
  { key: 'app_store_url', label: 'App Store link', placeholder: 'https://apps.apple.com/app/…' },
  { key: 'discord_url', label: 'Discord help link', placeholder: 'https://discord.gg/…' },
  { key: 'whatsapp_url', label: 'WhatsApp group link', placeholder: 'https://chat.whatsapp.com/…' },
]

export function OnboardingEditor({ eventId, event, onSaved }: Props) {
  const { toast } = useToast()
  const [form, setForm] = useState<EventOnboarding>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!event) return
    setForm((event.attributes.onboarding as EventOnboarding | null) ?? {})
  }, [event])

  const shareUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.streambridge.live'
    return `${origin}/v/${eventId}/onboarding`
  }, [eventId])

  function update(key: keyof EventOnboarding, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!event || saving) return
    setSaving(true)
    try {
      const res = await api.patch<{ data: EventResource }>(`/events/${eventId}`, {
        event: { onboarding: form },
      })
      toast({ title: 'Guide saved', tone: 'success' })
      onSaved?.(res.data)
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const errors = apiErr.response?.data?.error?.errors
      const detail =
        (Array.isArray(errors) ? errors.join('\n') : undefined) ??
        apiErr.response?.data?.error?.message ??
        'The onboarding guide couldn’t be saved. Try again.'
      toast({ title: 'Guide not saved', description: detail, tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="space-y-6">
      <Card
        title="Shareable onboarding link"
        description="Share this with sponsors and camera operators. Leave a field blank to use the default copy."
      >
        <div className="grid gap-s">
          <CopyField label="Onboarding link" value={shareUrl} />
          <ButtonLink variant="secondary" size="compact" href={shareUrl} target="_blank" rel="noreferrer" className="justify-self-start">
            <ExternalLink aria-hidden="true" />
            Open
          </ButtonLink>
        </div>
      </Card>

      {TEXT_FIELDS.map((field) => (
        <TextAreaField
          key={field.key}
          label={field.label}
          description={field.help}
          value={form[field.key] ?? ''}
          onChange={(e) => update(field.key, e.target.value)}
          rows={3}
          placeholder="Leave blank to use the default copy"
        />
      ))}

      <div className="grid gap-4 sm:grid-cols-2">
        {LINK_FIELDS.map((field) => (
          <TextField
            key={field.key}
            label={field.label}
            id={`onb-${field.key}`}
            type="url"
            value={form[field.key] ?? ''}
            onChange={(e) => update(field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        ))}
      </div>

      <div className="flex items-center justify-end pt-2">
        <Button type="submit" loading={saving} disabled={!event}>
          <Save aria-hidden="true" />
          Save guide
        </Button>
      </div>
    </form>
  )
}
